import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import ptPublic from "./locales/pt-BR/public.json";
import ptDashboard from "./locales/pt-BR/dashboard.json";
import enPublic from "./locales/en/public.json";
import enDashboard from "./locales/en/dashboard.json";
import esPublic from "./locales/es/public.json";
import esDashboard from "./locales/es/dashboard.json";

export const SUPPORTED_LOCALES = [
  { code: "pt-BR", label: "Português (Brasil)", htmlLang: "pt-BR", ogLocale: "pt_BR" },
  { code: "en", label: "English", htmlLang: "en", ogLocale: "en_US" },
  { code: "es", label: "Español", htmlLang: "es", ogLocale: "es_ES" },
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]["code"];
export const DEFAULT_LOCALE: SupportedLocale = "pt-BR";

export const localeMeta = (code: string) =>
  SUPPORTED_LOCALES.find((l) => l.code === code) ?? SUPPORTED_LOCALES[0];

const resources = {
  "pt-BR": { public: ptPublic, dashboard: ptDashboard },
  en: { public: enPublic, dashboard: enDashboard },
  es: { public: esPublic, dashboard: esDashboard },
} as const;

/**
 * Dual i18next instances:
 * - `publicI18n` drives strings on public routes; locale comes from the global
 *   default country (countries.is_default = true).
 * - `dashboardI18n` drives strings on admin/account routes; locale comes from
 *   the logged-in user's profile.dashboard_locale.
 *
 * Keeping them separate ensures an admin switching their own UI language never
 * changes what visitors see.
 */
function createInstance(ns: "public" | "dashboard") {
  const inst = i18n.createInstance();
  inst.use(initReactI18next).init({
    resources: Object.fromEntries(
      Object.entries(resources).map(([lng, r]) => [lng, { [ns]: (r as any)[ns] }]),
    ),
    lng: DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: ns,
    ns: [ns],
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  return inst;
}

export const publicI18n = createInstance("public");
export const dashboardI18n = createInstance("dashboard");

export function setPublicLocale(locale: string) {
  const code = SUPPORTED_LOCALES.find((l) => l.code === locale)?.code ?? DEFAULT_LOCALE;
  if (publicI18n.language !== code) publicI18n.changeLanguage(code);
}

export function setDashboardLocale(locale: string) {
  const code = SUPPORTED_LOCALES.find((l) => l.code === locale)?.code ?? DEFAULT_LOCALE;
  if (dashboardI18n.language !== code) dashboardI18n.changeLanguage(code);
}